import { motion } from 'framer-motion';
import Button from '../ui/Button';
import siteContent from '../../data/siteContent.json';

const Hero = () => {
    const content = siteContent.hero;
    return (
        <section id="home" className="relative h-[100svh] md:h-screen min-h-[520px] md:min-h-[600px] flex items-center justify-center overflow-hidden">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-primary-charcoal/55 md:bg-primary-charcoal/40 mix-blend-multiply z-10" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-black/85 md:from-primary-black/80 via-transparent to-transparent z-10" />

                {/* Placeholder for the actual hero image - using a reliable Unsplash ID for furniture/interior */}
                <picture>
                    <source
                        type="image/avif"
                        srcSet="/assets/images/unsplash-web-hero.avif"
                    />
                    <source
                        type="image/jpeg"
                        srcSet="/assets/images/unsplash-web-hero-640.jpg 640w, /assets/images/unsplash-web-hero-960.jpg 960w, /assets/images/unsplash-web-hero-1280.jpg 1280w, /assets/images/unsplash-web-hero-1920.jpg 1920w"
                        sizes="100vw"
                    />
                    <img
                        src="/assets/images/unsplash-web-hero-1280.jpg"
                        alt="Modern Mutfak Tasarımı"
                        width="1920"
                        height="1080"
                        loading="eager"
                        decoding="async"
                        fetchpriority="high"
                        className="w-full h-full object-cover"
                    />
                </picture>
            </div>

            {/* Content */}
            <div className="relative z-20 container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 items-center gap-8 md:gap-12 h-full pt-16 md:pt-20">
                {/* Text Content */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center md:text-left"
                >
                    <span className="block text-accent-gold font-bold tracking-[0.2em] mb-3 md:mb-4 uppercase text-sm md:text-base">
                        {content.badge}
                    </span>

                    <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-tight mb-4 md:mb-6">
                        {content.title.line1} <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary-beige to-white">
                            {content.title.line2}
                        </span>
                    </h1>

                    <p className="text-gray-200 text-base md:text-xl max-w-2xl mb-8 md:mb-10 font-light leading-relaxed mx-auto md:mx-0">
                        {content.description}
                    </p>

                    <div className="flex flex-col md:flex-row gap-3 md:gap-4 justify-center md:justify-start">
                        <Button variant="primary" onClick={() => document.getElementById('contact').scrollIntoView()}>
                            {content.cta.primary}
                        </Button>
                        <Button variant="outline" onClick={() => document.getElementById('gallery').scrollIntoView()}>
                            {content.cta.secondary}
                        </Button>
                    </div>
                </motion.div>

                {/* Decorative Watermark Logo */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className="hidden md:flex justify-end items-center opacity-20 pointer-events-none select-none"
                >
                    <img
                        src="/assets/logos/ERDE-512.png"
                        srcSet="/assets/logos/ERDE-256.png 256w, /assets/logos/ERDE-512.png 512w"
                        sizes="(min-width: 1024px) 384px, 256px"
                        alt="Decorative Logo"
                        width="512"
                        height="512"
                        loading="lazy"
                        decoding="async"
                        fetchpriority="low"
                        className="w-full max-w-md object-contain brightness-0 invert drop-shadow-2xl"
                    />
                </motion.div>
            </div>

            {/* Scroll Down Indicator */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1, duration: 1 }}
                className="absolute bottom-6 md:bottom-10 left-1/2 transform -translate-x-1/2 z-20 text-white flex flex-col items-center gap-2"
            >
                <span className="text-xs tracking-widest uppercase opacity-70">Aşağı Kaydır</span>
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="w-0.5 h-16 bg-gradient-to-b from-transparent via-white to-transparent"
                />
            </motion.div>
        </section>
    );
};

export default Hero;
